import {
  Checkbox,
  Group,
  Button,
  Space,
  Text,
  Notification,
  Tabs,
  Alert,
  Code,
} from '@mantine/core';
import {
  IconChecks,
  IconTemplate,
  IconDeviceFloppy,
  IconCopy,
  IconDragDrop,
  IconToml,
  IconAlertCircle,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react';
import { VisualSpecEditor } from './SpecEditor';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { generateHtmlFromSpecV2 } from '../../lib/form-spec-to-html';
import {
  FormSpec,
  parseAndValidateFormSpec,
  specsPlaceholder,
} from '../../lib/form-specs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FormSpecificationsProps {
  isEditing: boolean;
  isBusy: boolean;
  hasError: {
    title: string;
    message: string;
  } | null;
  submitSection: () => Promise<void>;
  // Initial values from parent (only used on mount and reset)
  initialSpecs: string | undefined;
  initialCheckSpecs: boolean;
  // Callback to report changes to parent
  onSpecsChange: (specs: string | undefined, checkSpecs: boolean) => void;
  formSubmitUrl: string;
  formChallengeUrl: string;
  formChallengeUrlNoTrack?: string;
  hasUnsavedChanges: boolean;
  onCancel: () => void;
  // Reset trigger - increment to reset local state
  resetKey?: number;

  // Hoisted from parent - parsed once
  parsedSpec: FormSpec | null;
  hasFormSpec: boolean;
}

export function FormSpecifications(props: FormSpecificationsProps) {
  const { t } = useTranslation();
  const [specErrors = new Map<string, string>(), setSpecErrors] = useState<Map<string, string>>();
  const [specIsValid, setSpecIsValid] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>('visual');

  // LOCAL STATE - owns specs and check_specs
  const [localSpecs, setLocalSpecs] = useState<string | undefined>(props.initialSpecs);
  const [localCheckSpecs, setLocalCheckSpecs] = useState(props.initialCheckSpecs);

  // Reset local state when resetKey changes (cancel operation)
  useEffect(() => {
    setLocalSpecs(props.initialSpecs);
    setLocalCheckSpecs(props.initialCheckSpecs);
  }, [props.resetKey]);

  // Report changes to parent
  const handleSpecsChange = (newSpecs: string | undefined) => {
    setLocalSpecs(newSpecs);
    props.onSpecsChange(newSpecs, localCheckSpecs);
  };

  const handleCheckSpecsChange = (checked: boolean) => {
    setLocalCheckSpecs(checked);
    props.onSpecsChange(localSpecs, checked);
  };

  // Find the field with spam checking enabled - memoize and use hoisted parsedSpec
  const spamCheckField = useMemo((): string | null => {
    if (!props.parsedSpec?.fields) return null;
    const spamField = Object.entries(props.parsedSpec.fields).find(
      ([_, field]) => field.check_spam
    );
    return spamField ? spamField[1].name || spamField[0] : null;
  }, [props.parsedSpec]);

  const changeTab = (value: string) => {
    if (props.hasUnsavedChanges) {
      alert(t('formSpec.unsavedChangesAlert'));
      return;
    }
    setCurrentTab(value);
  };

  const useTemplate = () => {
    handleSpecsChange(specsPlaceholder);
  };

  const checkSpecs = () => {
    try {
      const res = parseAndValidateFormSpec(localSpecs);
      if (!res.isValid) {
        setSpecErrors(res.errors);
        setSpecIsValid(false);
      } else {
        setSpecErrors(new Map());
        setSpecIsValid(true);
      }
    } catch (e) {
      const errorMsg = (e as any).message;
      setSpecErrors(new Map([['error', errorMsg]]));
    }
  };

  const copyHtmlToClipboard = () => {
    if (props.parsedSpec) {
      const html = generateHtmlFromSpecV2(
        props.parsedSpec,
        props.formSubmitUrl,
        localCheckSpecs,
        props.formChallengeUrl
      );
      navigator.clipboard.writeText(html);
      alert(t('formSpec.htmlCopied'));
    }
  };
  return (
    <>
      {spamCheckField && (
        <Alert color="yellow" icon={<IconAlertCircle size={20} />} mb="xs" mt="xs">
          {t('formSpec.spamFilterEnabledWithField')} <Code>{spamCheckField}</Code>.
        </Alert>
      )}
      {!localCheckSpecs && (
        <Alert color="primary" icon={<IconInfoCircle size={20} />} mb="xs" mt="xs">
          {t('formSpec.formFieldsDisabled')}
        </Alert>
      )}

      <Checkbox
        label={t('formSpec.checkFormFields')}
        checked={localCheckSpecs}
        onChange={(event) => handleCheckSpecsChange(event.currentTarget.checked)}
        mb="xs"
        description={t('formSpec.checkFormFieldsDescription')}
      />

      {props.hasUnsavedChanges && (
        <Alert color="yellow" icon={<IconAlertCircle size={20} />} mb="xs" mt="xs">
          {t('formSpec.unsavedChanges')}
        </Alert>
      )}

      <Tabs
        defaultValue="visual"
        onChange={(value) => changeTab(value || 'visual')}
        value={currentTab}
      >
        <Tabs.List>
          <Tabs.Tab value="visual" leftSection={<IconDragDrop size={12} />}>
            {t('formSpec.visualEditor')}
          </Tabs.Tab>
          <Tabs.Tab value="code" leftSection={<IconToml size={12} />}>
            {t('formSpec.toml')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="visual">
          <VisualSpecEditor
            spec={localSpecs}
            onChange={handleSpecsChange}
          />
        </Tabs.Panel>

        <Tabs.Panel value="code">
          <>
            <CodeEditor
              value={localSpecs}
              language="js"
              placeholder="Enter your form specification, or use template."
              onChange={(evn) => handleSpecsChange(evn.target.value)}
              padding={15}
              style={{
                fontFamily:
                  'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace',
              }}
            />
            {specErrors?.size > 0 && (
              <Notification title={t('formSpec.specsErrors')} color="red" mb="md" mt="sm">
                {Array.from(specErrors || []).map(([key, value]) => (
                  <div key={`spec-error-${key}`}>
                    <Text c="red">
                      {key}: {value}
                    </Text>
                  </div>
                ))}
              </Notification>
            )}

            {specIsValid && (
              <Notification title={t('formSpec.noErrors')} color="green" mb="md" mt="sm">
                <Text color="green">{t('formSpec.specsValid')}</Text>
              </Notification>
            )}

            <Group>
              <Button
                onClick={checkSpecs}
                mt="sm"
                size="sm"
                variant="light"
                leftSection={<IconChecks />}
              >
                {t('formSpec.checkValidity')}
              </Button>
              <Button
                onClick={useTemplate}
                mt="sm"
                size="sm"
                variant="subtle"
                leftSection={<IconTemplate />}
              >
                {t('formSpec.prefillExample')}
              </Button>
            </Group>
          </>
        </Tabs.Panel>
      </Tabs>

      <Space h="md" mt="md" />
      <Group>
        <Button
          onClick={props.submitSection}
          loading={props.isBusy}
          disabled={props.isBusy}
          leftSection={<IconDeviceFloppy />}
          color={props.hasUnsavedChanges ? 'yellow' : ''}
        >
          {props.isEditing
            ? props.hasUnsavedChanges
              ? t('glob_buttons.updateUnsaved')
              : t('glob_buttons.update')
            : t('glob_buttons.create')}
        </Button>
        {props.hasUnsavedChanges && (
          <Button
            onClick={props.onCancel}
            loading={props.isBusy}
            disabled={props.isBusy}
            leftSection={<IconX />}
            color="red"
          >
            {t('glob_buttons.cancel')}
          </Button>
        )}
        {props.isEditing && (
          <Button
            onClick={copyHtmlToClipboard}
            loading={props.isBusy}
            disabled={props.isBusy || !localSpecs}
            leftSection={<IconCopy />}
            variant="light"
          >
            {t('formSpec.copyHtmlForm')}
          </Button>
        )}
      </Group>

      {props.hasError && (
        <Alert mt="md" icon={<IconAlertCircle size={16} />} title={props.hasError.title} color="red">
          {props.hasError.message}
        </Alert>
      )}
    </>
  );
}
